import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { AlarmClock, Check, X } from 'lucide-react';
import { useI18n } from '../../contexts/I18nContext';
import { useReminders, useUpdateReminder } from '../../hooks/useReminders';
import { useTimerNow } from '../../hooks/useTaskTimers';
import { formatDueChip, isReminderFiring, plusMinutes, tomorrowAt9 } from '../../lib/reminderDates';
import { Popover } from '../tasks/linear/Popover';
import type { Reminder } from '../../lib/remindersApi';

/**
 * The global red reminder strip — a structural sibling of the TimerBar, pinned
 * right below it on every route. A reminder fires 10 minutes before its dueAt
 * and its row stays until the user acts: Finalizat (done), Amână (moves dueAt,
 * which re-arms it server-side) or X (ack — silenced for this firing only).
 *
 * Red on purpose: the timer bar owns green/amber, and a pre-due reminder is
 * exactly the "you are about to miss something" case red is reserved for.
 */
const PALETTE = {
  bar: 'border-red-500/40 bg-red-500/10',
  text: 'text-red-600',
  dot: 'bg-red-500',
  button: 'text-red-600 hover:bg-red-500/15 hover:text-red-700',
} as const;

function SnoozeMenu({ reminder, nowMs }: { reminder: Reminder; nowMs: number }) {
  const { t } = useI18n();
  const update = useUpdateReminder();

  // Targets are computed from the corrected `nowMs`, not the old dueAt: an
  // hours-overdue reminder snoozed "+10 min" must fire 10 minutes from NOW.
  const options = [
    { label: t('reminders.snooze10m'), dueAt: () => plusMinutes(nowMs, 10) },
    { label: t('reminders.snooze1h'), dueAt: () => plusMinutes(nowMs, 60) },
    { label: t('reminders.snoozeTomorrow'), dueAt: () => tomorrowAt9(nowMs) },
  ];

  return (
    <Popover
      width={180}
      align="end"
      trigger={({ open, toggle, ref }) => (
        <button
          ref={ref}
          type="button"
          disabled={update.isPending}
          onClick={toggle}
          aria-haspopup="menu"
          aria-expanded={open}
          title={t('reminders.snooze')}
          className={`inline-flex items-center gap-1 rounded px-1.5 py-1 text-[12px] disabled:opacity-50 ${PALETTE.button}`}
        >
          <AlarmClock size={13} />
          <span>{t('reminders.snooze')}</span>
        </button>
      )}
    >
      {(close) => (
        <div className="py-1">
          {options.map((option) => (
            <button
              key={option.label}
              type="button"
              onClick={() => {
                update.mutate({ id: reminder.id, patch: { dueAt: option.dueAt() } });
                close();
              }}
              className="block w-full px-3 py-1.5 text-left text-[13px] text-foreground hover:bg-accent"
            >
              {option.label}
            </button>
          ))}
        </div>
      )}
    </Popover>
  );
}

function ReminderRow({ reminder, nowMs }: { reminder: Reminder; nowMs: number }) {
  const { t, locale } = useI18n();
  const update = useUpdateReminder();
  const btn = `inline-flex items-center gap-1 rounded px-1.5 py-1 text-[12px] disabled:opacity-50 ${PALETTE.button}`;

  return (
    <div
      className={`flex min-w-0 items-center gap-2 border-b px-3 py-1.5 md:border-r ${PALETTE.bar}`}
    >
      <span className={`h-2 w-2 shrink-0 animate-pulse rounded-full ${PALETTE.dot}`} />
      <Link
        to="/reminders"
        className="min-w-0 flex-1 truncate text-[13px] text-gray-800 hover:underline"
      >
        <span className="font-medium">{reminder.title}</span>
        {reminder.description && <span className="ml-1.5 opacity-60">{reminder.description}</span>}
      </Link>
      <span
        className={`hidden shrink-0 text-[12px] font-semibold tabular-nums sm:inline ${PALETTE.text}`}
      >
        {formatDueChip(reminder.dueAt, locale, {
          today: t('reminders.today'),
          tomorrow: t('reminders.tomorrow'),
        })}
      </span>
      <div className="flex shrink-0 items-center gap-0.5">
        <button
          type="button"
          disabled={update.isPending}
          onClick={() => update.mutate({ id: reminder.id, patch: { done: true } })}
          title={t('reminders.markDone')}
          className={btn}
        >
          <Check size={13} />
          <span>{t('reminders.done')}</span>
        </button>
        <SnoozeMenu reminder={reminder} nowMs={nowMs} />
        <button
          type="button"
          disabled={update.isPending}
          onClick={() => update.mutate({ id: reminder.id, patch: { ack: true } })}
          title={t('reminders.dismiss')}
          aria-label={t('reminders.dismiss')}
          className={btn}
        >
          <X size={13} />
        </button>
      </div>
    </div>
  );
}

/**
 * Same rule as the TimerBar: several reminders flow into columns — two from
 * `md`, three from `xl` — so a busy morning does not stack a screen of rows.
 */
function gridColumns(count: number): string {
  if (count <= 1) return 'grid-cols-1';
  if (count === 2) return 'grid-cols-1 md:grid-cols-2';
  return 'grid-cols-1 md:grid-cols-2 xl:grid-cols-3';
}

export function ReminderBar() {
  const { data, refetch } = useReminders();
  // Server-corrected clock, ticking every second, so the bar appears within a
  // second of crossing the 10-minute threshold — not at the next poll.
  const nowMs = useTimerNow(data?.serverNow);

  // Coming back to the tab: the interval was throttled while hidden, so pull
  // fresh state instead of trusting a possibly-acked reminder.
  useEffect(() => {
    function onVisible() {
      if (document.visibilityState === 'visible') void refetch();
    }
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [refetch]);

  // The firing rule lives in lib/reminderDates, shared with the calendar.
  const firing = (data?.reminders ?? []).filter((r) => isReminderFiring(r, nowMs));
  if (firing.length === 0) return null;

  // Server order is dueAt asc, so the most urgent row comes first.
  return (
    <div className={`grid shrink-0 ${gridColumns(firing.length)}`}>
      {firing.map((reminder) => (
        <ReminderRow key={reminder.id} reminder={reminder} nowMs={nowMs} />
      ))}
    </div>
  );
}
